import { waitedWords } from "@minga/core"
import type { HomeData } from "@minga/protocol"
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useState } from "react"
import { homeNeedsCount, moreInProjects, rowKindWord, rowTitle, taskKindWord } from "@/features/home/model"
import { homeQuery } from "@/lib/settings-api"
import { cn } from "@/lib/utils"
import { needsYouSummaryQuery } from "@/lib/workspace-api"
import { INBOX_LINES } from "@/model/glossary"
import { Badge } from "./ui/badge"
import { Popover } from "./ui/popover"

/**
 * "Needs you 3" in the workspace header (H10 count, or Home's count with the projects' own tallies once Home is
 * loaded), hidden at 0. UX pass phase 2 (P13): it opens the list of what waits on you across projects (Home no longer
 * repeats it); each row opens the card in its project's Needs you.
 */
export function NeedsYouChip({ workspaceId, className }: { workspaceId: string; className?: string }) {
  const [open, setOpen] = useState(false)
  const cards = useQuery(needsYouSummaryQuery(workspaceId)).data?.count ?? 0
  // Home's data when it is loaded, or once the list is opened (never fetched only for the count)
  const home = useQuery({ ...homeQuery(workspaceId), enabled: open })
  const count = home.data ? Math.max(cards, homeNeedsCount(home.data)) : cards
  if (count === 0) return null
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title="Needs you"
      align="end"
      role="dialog"
      className="w-[min(26rem,calc(100vw-2rem))]"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          className={cn(
            "tap flex h-8 items-center gap-2 rounded-control border border-needs bg-needs-soft px-2.5 text-[13px] font-semibold text-needs hover:text-needs",
            className,
          )}
          aria-label={`Needs you: ${count} waiting`}
          title="Decisions and to-dos that wait on you"
        >
          <span className="max-sm:sr-only">Needs you</span>
          <span className="count-badge min-w-4 rounded-pill bg-needs px-1 text-center text-on-needs">
            {count}
          </span>
        </button>
      )}
    >
      <div className="flex flex-col gap-1 p-2">
        <h2 className="font-semibold">Needs you</h2>
        <p className="type-meta m-0" data-inbox-line="">
          {INBOX_LINES.needs}
        </p>
      </div>
      <div className="max-h-[60dvh] overflow-y-auto p-1">
        {home.isPending && (
          <p role="status" className="type-meta px-2 py-1.5">
            Loading what waits on you…
          </p>
        )}
        {home.isError && !home.data && (
          <p role="alert" className="type-meta px-2 py-1.5">
            Couldn't load the list. The count is {count}.
          </p>
        )}
        {home.data && (
          <NeedsYouRows home={home.data} workspaceId={workspaceId} onChoose={() => setOpen(false)} />
        )}
      </div>
    </Popover>
  )
}

/** The rows: up to 8 cards, then "N more" per project, each opening that project's Needs you. */
export function NeedsYouRows({
  home,
  workspaceId,
  onChoose,
}: {
  home: HomeData
  workspaceId: string
  onChoose?: () => void
}) {
  const data = home.needsYou
  const more = moreInProjects(home)
  const now = home.generatedAt
  if (homeNeedsCount(home) === 0)
    return (
      <p className="type-meta px-2 py-1.5">Nothing needs you. Agents ask here when they need a decision.</p>
    )
  return (
    <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
      {data.items.slice(0, 8).map((item) => (
        <li key={`${item.projectId}:${item.proposalId}`}>
          <Link
            to="/w/$workspaceId/p/$projectId"
            params={{ workspaceId, projectId: item.projectId }}
            search={{ view: "needs", card: item.proposalId }}
            onClick={onChoose}
            className="tap flex items-center gap-2 rounded-control px-2 py-1.5 text-sm text-ink no-underline hover:bg-fill hover:text-ink"
          >
            <Badge tone="needs">{rowKindWord(item)}</Badge>
            <span className="min-w-0 flex-1 truncate">{rowTitle(item)}</span>
            <span className="mono-data shrink-0 text-muted">{item.projectName}</span>
          </Link>
        </li>
      ))}
      {/* decision 0163: items whose next move is yours (yours to do, close, answer or sort), oldest first, with age */}
      {(data.tasks ?? []).slice(0, 8).map((task) => (
        <li key={`${task.projectId}:${task.itemId}`}>
          <Link
            to="/w/$workspaceId/p/$projectId/i/$ref"
            params={{ workspaceId, projectId: task.projectId, ref: task.ref }}
            onClick={onChoose}
            className="tap flex items-center gap-2 rounded-control px-2 py-1.5 text-sm text-ink no-underline hover:bg-fill hover:text-ink"
            data-needs-task={task.why}
          >
            <Badge tone="needs">{taskKindWord(task)}</Badge>
            <span className="min-w-0 flex-1 truncate">
              <span className="mono-data text-muted">{task.ref}</span> {task.title}
            </span>
            <span
              className="mono-data shrink-0 text-muted"
              title={`waiting ${waitedWords(now - task.since)}`}
            >
              {waitedWords(now - task.since)} · {task.projectName}
            </span>
          </Link>
        </li>
      ))}
      {more.map((m) => (
        <li key={`more:${m.projectId}`}>
          <Link
            to="/w/$workspaceId/p/$projectId"
            params={{ workspaceId, projectId: m.projectId }}
            search={{ view: "needs" }}
            onClick={onChoose}
            className="tap flex items-center gap-2 rounded-control px-2 py-1.5 text-sm text-ink-2 no-underline hover:bg-fill hover:text-ink"
          >
            <span className="min-w-0 flex-1 truncate">
              {data.items.some((i) => i.projectId === m.projectId)
                ? `${m.more} more in Needs you`
                : `${m.more} ${m.more === 1 ? "thing waits" : "things wait"} on you`}
            </span>
            <span className="mono-data shrink-0 text-muted">{m.projectName}</span>
          </Link>
        </li>
      ))}
      {data.partial.length > 0 && (
        <li className="px-2 pt-1 text-xs text-muted">
          {data.partial.length} project{data.partial.length === 1 ? "" : "s"} didn't answer in time; the count
          may be low.
        </li>
      )}
    </ul>
  )
}
